import PlayArrowIcon from '@material-symbols/svg-300/rounded/play_arrow.svg'
import VideoCallIcon from '@material-symbols/svg-300/rounded/video_call.svg'
import { isAfter } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { LearningItem } from '../../models/LearningItem'
import bookingForLearningItem from '../../state/calculations/bookingForLearningItem'
import learningItemTimeInvested from '../../state/calculations/learningItemTimeInvested'
import { userCanBookLearningItemBeforeDueDate } from '../../state/calculations/permissions/userCan'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import Button from '../basics/Button/Button'

type StartCourseLinkProps = { learningItem: LearningItem; fallbackToCoursePage?: boolean } & Omit<
  React.ComponentProps<typeof Button>,
  'component' | 'to' | 'children'
>

export default React.memo(function StartCourseLink({
  learningItem,
  fallbackToCoursePage,
  ...buttonProps
}: StartCourseLinkProps) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const booking = bookingForLearningItem(learningItem)
  if (booking) {
    if (learningItem.kind === 'onDemandCourse') {
      return (
        <Button component={Link} to={routes.playCourse.url({ uuid: learningItem.uuid })} {...buttonProps}>
          {learningItemTimeInvested(learningItem) > 0 ? t('learning item.continue') : t('learning item.start')}
          <PlayArrowIcon />
        </Button>
      )
    }

    if (learningItem.kind === 'scheduledCourse') {
      const now = new Date()
      const event = learningItem.scheduledCourse.events.find(({ uuid }) => uuid === booking.scheduledCourseEventUuid)
      const date = (event?.dates || []).find(({ endDate }) => isAfter(endDate, now))
      if (date && date.meetingLink) {
        return (
          <Button component="a" target="_blank" href={date.meetingLink} {...buttonProps}>
            {t('learning item.join')}
            <VideoCallIcon />
          </Button>
        )
      }
    }
  }

  if (fallbackToCoursePage) {
    if (!booking && userCanBookLearningItemBeforeDueDate(currentUser, learningItem)) {
      return (
        <Button component={Link} to={routes.course.url({ uuid: learningItem.uuid })} {...buttonProps} filled={false}>
          {t('learning item.book')}
        </Button>
      )
    }

    return (
      <Button component={Link} to={routes.course.url({ uuid: learningItem.uuid })} {...buttonProps} filled={false}>
        {t('learning item.view')}
      </Button>
    )
  }

  return null
})
